/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import {
  DataRowRecord,
  DataTableProps,
  DataTableState,
  Actions,
  SelectAllRowsAction,
  SelectSingleRowAction,
  SelectMultipleRowsAction,
  SortAction,
} from '../types';

import { dataTableReducer } from '../reducer/dataTable.reducer';

import DataTableWrapper from './DataTableWrapper';
import DataTableTable from './DataTableTable';
import DataTableHead from './DataTableHead';
import DataTableHeadRow from './DataTableHeadRow';
import DataTableCol from './DataTableCol';
import DataTableBody from './DataTableBody';
import DataTableRow from './DataTableRow';
import DataColCheckbox from './DataColCheckbox';
import DataPagination from './DataPagination';

import useDidUpdateEffect from '../hooks/useDidUpdateEffect';
import {
  isRowSelected,
  nullFn,
  uniqueifyDataColumns,
  isEmpty,
  getNumberOfPages,
  recalculatePage,
  getSortDirection,
  getColumnById,
  sort,
} from '../utils';
import DataTableHeader from './DataTableHeader';
import { Loader } from '../../loader';
import { Heading } from '../../heading';

function DataTable<T extends DataRowRecord>({
  columns = [],
  data = [],
  keyField = 'id',
  onSelectedRowsChange = nullFn,
  selectableRows,
  selectableRowsDisabled = null,
  selectableRowsHighlight = true,
  selectableRowsNoSelectAll = false,
  selectableRowsSelected = null,
  selectableRowsVisibleOnly = false,
  singleSelectRow = false,
  actions = null,
  filterActions = null,
  title = '',
  noHead = false,
  noHeader = false,
  pagination = false,
  paginationServer = false,
  onChangePage = nullFn,
  paginationServerOptions = {
    persistSelectedOnPageChange: false,
    persistSelectedOnSort: false,
  },
  paginationDefaultPage = 1,
  paginationResetDefaultPage = false,
  paginationPerPage = 7,
  paginationTotalRows = 0,
  noContextMenu = false,
  contextMessage = { singular: 'item', plural: 'items', message: 'selected' },
  defaultMessage = undefined,
  defaultSortAsc = true,
  defaultSortField = null,
  onSort = nullFn,
  sortFunction = null,
  sortServer = false,
  clearSelectedRows = false,
  tooltip = undefined,
  search = false,
  searchPrompt,
  onSearchChange = nullFn,
  filterItems = [],
  hiddenDropDown = false,
  dropDownItems = [],
  dropDown = false,
  className = undefined,
  isLoading = false,
}: DataTableProps<T>): JSX.Element {
  const dataColumnsMemo = React.useMemo(
    () => uniqueifyDataColumns<T>(columns),
    [columns],
  );

  const defaultSortDirection = getSortDirection(defaultSortAsc);
  const defaultSortColumn = React.useMemo(
    () => getColumnById<T>(defaultSortField, dataColumnsMemo),
    [dataColumnsMemo, defaultSortField],
  );

  const initialState: DataTableState<T> = React.useMemo(
    () => ({
      allSelected: false,
      rows: defaultSortColumn?.selector
        ? sort(
            data,
            defaultSortColumn.selector,
            defaultSortDirection,
            sortFunction,
          )
        : data,
      selectedCount: 0,
      selectedRows: [],
      singleSelect: singleSelectRow,
      currentPage: paginationDefaultPage,
      rowsPerPage: paginationPerPage,
      selectedRowsFlag: false,
      contextMessage: {
        singular: 'item',
        plural: 'items',
        message: 'selected',
      },
      selectedColumn: defaultSortColumn || { name: '' },
      sortDirection: defaultSortDirection,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [
    {
      rows,
      allSelected,
      singleSelect,
      selectedCount,
      selectedRows,
      rowsPerPage,
      currentPage,
      selectedColumn,
      sortDirection,
    },
    dispatch,
  ] = React.useReducer<React.Reducer<DataTableState<T>, Actions<T>>>(
    dataTableReducer,
    initialState,
  );

  const { persistSelectedOnPageChange = false, persistSelectedOnSort = false } =
    paginationServerOptions;
  const mergeSelections = !!(
    paginationServer &&
    (persistSelectedOnPageChange || persistSelectedOnSort)
  );
  const showPagination = pagination && data.length > 0;

  const calculatedRows = React.useMemo(() => {
    if (pagination && !paginationServer) {
      const lastIndex = currentPage * rowsPerPage;
      const firstIndex = lastIndex - rowsPerPage;

      return rows.slice(firstIndex, lastIndex);
    }

    return rows;
  }, [currentPage, pagination, paginationServer, rows, rowsPerPage]);

  const handleSort = (action: SortAction<T>) => {
    dispatch(action);
  };

  const handleSelectAllRows = (action: SelectAllRowsAction<T>) => {
    dispatch(action);
  };

  const handelSelectRow = (action: SelectSingleRowAction<T>) => {
    dispatch(action);
  };

  const handleChangePage = (page: number) =>
    dispatch({
      type: 'CHANGE_PAGE',
      page,
      paginationServer,
      visibleOnly: selectableRowsVisibleOnly,
      persistSelectedOnPageChange,
    });

  if (
    pagination &&
    !paginationServer &&
    rows.length > 0 &&
    calculatedRows.length === 0
  ) {
    const updatePage = getNumberOfPages(rows.length, rowsPerPage);
    const recalculatedPage = recalculatePage(currentPage, updatePage);

    handleChangePage(recalculatedPage);
  }

  useDidUpdateEffect(() => {
    onSelectedRowsChange({ allSelected, selectedCount, selectedRows });
  }, [selectedCount]);

  useDidUpdateEffect(() => {
    onChangePage(currentPage, paginationTotalRows || rows.length);
  }, [currentPage]);

  useDidUpdateEffect(() => {
    handleChangePage(paginationDefaultPage);
  }, [paginationDefaultPage, paginationResetDefaultPage]);

  useDidUpdateEffect(() => {
    onSort(selectedColumn, sortDirection);
  }, [selectedColumn, sortDirection]);

  useDidUpdateEffect(() => {
    if (pagination && paginationServer && paginationTotalRows > 0) {
      const updatePage = getNumberOfPages(paginationTotalRows, rowsPerPage);
      const recalculatedPage = recalculatePage(currentPage, updatePage);
      if (currentPage !== recalculatedPage) {
        handleChangePage(recalculatedPage);
      }
    }
  }, [paginationTotalRows]);

  React.useEffect(() => {
    dispatch({
      type: 'UPDATE_ROWS',
      rows: defaultSortColumn?.selector
        ? sort(
            data,
            defaultSortColumn.selector,
            defaultSortDirection,
            sortFunction,
          )
        : data,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  React.useEffect(() => {
    dispatch({
      type: 'CLEAR_SELECTED_ROWS',
      selectedRowsFlag: clearSelectedRows,
    });
  }, [clearSelectedRows]);

  React.useEffect(() => {
    if (selectableRowsSelected) {
      const preSelectedRows = rows.filter((row) => selectableRowsSelected(row));

      dispatch({
        type: 'SELECT_MULTIPLE_ROWS',
        keyField: keyField,
        selectedRows: preSelectedRows,
        rows: rows,
        mergeSelections,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const showSelectAll = selectableRowsNoSelectAll;

  const displayHead = () => {
    if (noHead) {
      return false;
    }

    return true;
  };

  const displayHeader = () => {
    if (noHeader) {
      return false;
    }

    if (title) {
      return true;
    }

    if (actions) {
      return true;
    }

    return false;
  };

  const rowData = selectableRowsVisibleOnly ? calculatedRows : rows;

  return (
    <DataTableWrapper className={className}>
      {displayHeader() && (
        <DataTableHeader
          title={title}
          actions={actions}
          filterActions={filterActions}
          showMenu={!noContextMenu}
          selectedCount={selectedCount}
          contextMessage={contextMessage}
          onSort={handleSort}
          tooltip={tooltip}
          search={search}
          searchPropmt={searchPrompt}
          onSearchChange={onSearchChange}
          items={filterItems}
          hiddenDropDown={hiddenDropDown}
          dropDownItems={dropDownItems}
          dropDown={dropDown}
        />
      )}
      <DataTableTable>
        {displayHead() && (
          <DataTableHead>
            <DataTableHeadRow>
              {selectableRows ? (
                showSelectAll ? (
                  <DataColCheckbox
                    allSelected={allSelected}
                    selectedRows={selectedRows}
                    rowData={rowData}
                    keyField={keyField}
                    mergeSelections={mergeSelections}
                    onSelectAllRows={handleSelectAllRows}
                    selectableRowDisabled={selectableRowsDisabled}
                  />
                ) : (
                  <div style={{ flex: '0 0 48px' }}></div>
                )
              ) : (
                <div style={{ flex: '0 0 10px' }}></div>
              )}
              {dataColumnsMemo.map((col) => (
                <DataTableCol column={col} key={col.id} rows={rows} />
              ))}
            </DataTableHeadRow>
          </DataTableHead>
        )}

        <DataTableBody>
          {isLoading ? (
            <div className='flex w-full flex-row items-center justify-center py-6'>
              <Loader size='SMALL' color='GREEN' />
              <p className='ml-2'>Loading...</p>
            </div>
          ) : null}
          {!isLoading &&
            calculatedRows.map((row, idx) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              const id = isEmpty(row[keyField]) ? idx : row[keyField];
              const selected = isRowSelected(row, selectedRows, 'id');

              return (
                <DataTableRow
                  columns={dataColumnsMemo}
                  id={id}
                  key={id}
                  row={row}
                  keyField={keyField}
                  selectableRows={selectableRows}
                  selectableRowsHighlight={selectableRowsHighlight}
                  onSelectedRow={handelSelectRow}
                  singleSelect={singleSelect}
                  selectedCount={selectedCount}
                  rowCount={rows.length}
                  rowIdx={idx}
                  selectableRowsDisabled={selectableRowsDisabled}
                  selected={selected}
                />
              );
            })}
          {!isLoading && calculatedRows.length === 0 ? (
            <div className='flex w-full flex-row items-center justify-center py-6'>
              {defaultMessage ? (
                <p>{defaultMessage}</p>
              ) : (
                <p>No Current Results</p>
              )}
            </div>
          ) : null}
        </DataTableBody>
      </DataTableTable>

      {showPagination && (
        <DataPagination
          onChangePage={handleChangePage}
          currentPage={currentPage}
          rowCount={paginationTotalRows || rows.length}
          rowsPerPage={rowsPerPage}
        ></DataPagination>
      )}
    </DataTableWrapper>
  );
}

export default DataTable;
